/*
  Resorts Depot navigation: desktop mega menu + mobile drawer.
  Plain JavaScript, no dependencies. Empire's own header script keeps handling
  the sticky header, live search and the mobile panel open/close, overlay and
  scroll lock; this file only drives the new markup (data-rd-* attributes).

  Mobile drawer motion (Web Animations API, so start and end frames are
  explicit and nothing depends on class-toggle timing):
    - Departments push in from the right, and Back pushes them back out, with
      the level underneath shifted and dimmed so the hierarchy reads.
    - Groups inside a department expand in place, one at a time, by animating
      the panel's rendered height between its closed and open values.
    - Everything collapses to an instant swap under prefers-reduced-motion.
*/
(function () {
  'use strict';

  var DESKTOP_MIN = 1024;
  var OPEN_DELAY = 90;
  var CLOSE_DELAY = 180;
  var PUSH_MS = 280;
  var PUSH_EASE = 'cubic-bezier(.32,.72,0,1)';
  var ACC_MS = 240;
  var ACC_EASE = 'cubic-bezier(.4,0,.2,1)';

  function isDesktop() {
    return window.matchMedia('(min-width: ' + DESKTOP_MIN + 'px)').matches;
  }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function canAnimate(el) {
    return !reducedMotion() && typeof el.animate === 'function';
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function focusSafely(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (error) { el.focus(); }
  }

  /* ------------------------------------------------------------------ */
  /* Desktop mega menu                                                   */
  /* ------------------------------------------------------------------ */
  function MegaNav(root) {
    if (root.__rdNav) return root.__rdNav;
    root.__rdNav = this;
    this.root = root;
    this.items = toArray(root.querySelectorAll('[data-rd-nav-item]'));
    this.scrim = root.querySelector('[data-rd-nav-scrim]');
    this.header = root.closest('[data-site-header]') || root.closest('header') || root;
    this.open = null;
    this.openTimer = null;
    this.closeTimer = null;
    this.bind();
  }

  MegaNav.prototype.bind = function () {
    var self = this;

    this.items.forEach(function (item) {
      var trigger = item.querySelector('[data-rd-nav-trigger]');
      var panel = item.querySelector('[data-rd-nav-panel]');
      if (!trigger || !panel) return;
      item.__trigger = trigger;
      item.__panel = panel;

      trigger.addEventListener('mouseenter', function () { self.scheduleOpen(item); });
      trigger.addEventListener('focus', function () { self.openItem(item); });
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        if (self.open === item) {
          self.closeAll();
        } else {
          self.openItem(item);
        }
      });
      panel.addEventListener('mouseenter', function () { self.cancelClose(); });
    });

    this.root.addEventListener('mouseleave', function () { self.scheduleClose(); });
    this.root.addEventListener('mouseenter', function () { self.cancelClose(); });

    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.open) {
        var trigger = self.open.__trigger;
        self.closeAll();
        trigger.focus();
      }
    });

    this.root.addEventListener('focusout', function (event) {
      if (!self.open) return;
      var next = event.relatedTarget;
      if (!next || !self.root.contains(next)) self.closeAll();
    });

    document.addEventListener('click', function (event) {
      if (self.open && !self.root.contains(event.target)) self.closeAll();
    });

    window.addEventListener('resize', function () {
      if (!self.open) return;
      if (!isDesktop()) {
        self.closeAll();
      } else {
        self.position(self.open);
        self.positionScrim();
      }
    });

    window.addEventListener('scroll', function () {
      if (self.open) self.positionScrim();
    }, { passive: true });
  };

  MegaNav.prototype.scheduleOpen = function (item) {
    var self = this;
    this.cancelClose();
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(function () { self.openItem(item); }, OPEN_DELAY);
  };

  MegaNav.prototype.scheduleClose = function () {
    var self = this;
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(function () { self.closeAll(); }, CLOSE_DELAY);
  };

  MegaNav.prototype.cancelClose = function () {
    clearTimeout(this.closeTimer);
  };

  MegaNav.prototype.openItem = function (item) {
    if (!isDesktop() || !item.__panel) return;
    clearTimeout(this.openTimer);
    this.cancelClose();
    if (this.open === item) return;
    if (this.open) this.closeItem(this.open);

    this.open = item;
    item.classList.add('is-open');
    item.__trigger.setAttribute('aria-expanded', 'true');
    item.__panel.hidden = false;
    item.__panel.classList.add('is-open');
    this.position(item);

    if (this.scrim) {
      if (item.getAttribute('data-rd-nav-kind') === 'compact') {
        this.scrim.classList.remove('is-visible');
      } else {
        this.positionScrim();
        this.scrim.classList.add('is-visible');
      }
    }
  };

  MegaNav.prototype.closeItem = function (item) {
    item.classList.remove('is-open');
    item.__trigger.setAttribute('aria-expanded', 'false');
    item.__panel.classList.remove('is-open');
    item.__panel.hidden = true;
  };

  MegaNav.prototype.closeAll = function () {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    if (this.open) this.closeItem(this.open);
    this.open = null;
    if (this.scrim) this.scrim.classList.remove('is-visible');
  };

  /* Full-width panels span the viewport regardless of the header's own
     container; compact panels sit under their trigger and stay on screen. */
  MegaNav.prototype.position = function (item) {
    var panel = item.__panel;
    var navRect = this.root.getBoundingClientRect();
    var docWidth = document.documentElement.clientWidth;

    if (item.getAttribute('data-rd-nav-kind') === 'compact') {
      var triggerRect = item.__trigger.getBoundingClientRect();
      var left = triggerRect.left - navRect.left;
      panel.style.width = '';
      panel.style.left = left + 'px';
      var panelRect = panel.getBoundingClientRect();
      var overflow = panelRect.right - (docWidth - 16);
      if (overflow > 0) panel.style.left = Math.max(0, left - overflow) + 'px';
    } else {
      panel.style.left = (-navRect.left) + 'px';
      panel.style.width = docWidth + 'px';
    }
  };

  MegaNav.prototype.positionScrim = function () {
    if (!this.scrim) return;
    var headerRect = this.header.getBoundingClientRect();
    this.scrim.style.top = Math.max(0, headerRect.bottom) + 'px';
  };

  /* ------------------------------------------------------------------ */
  /* Mobile drawer                                                       */
  /* ------------------------------------------------------------------ */
  function Drawer(root) {
    if (root.__rdDrawer) return root.__rdDrawer;
    root.__rdDrawer = this;
    this.root = root;
    this.host = root.closest('[data-mobile-nav]');
    this.panel = root.closest('[data-mobile-nav-panel]') || root;
    this.stage = root.querySelector('[data-rd-stage]');
    this.foot = root.querySelector('.rd-drawer__foot');
    this.levels = {};
    this.stack = [];
    this.openers = {};
    this.current = 'root';
    this.animating = null;

    var self = this;
    toArray(root.querySelectorAll('[data-rd-level]')).forEach(function (level) {
      self.levels[level.getAttribute('data-rd-level')] = level;
    });
    this.bind();
  }

  Drawer.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var go = event.target.closest('[data-rd-go]');
      if (go && self.root.contains(go)) {
        event.preventDefault();
        self.go(go.getAttribute('data-rd-go'), go);
        return;
      }
      var back = event.target.closest('[data-rd-back]');
      if (back && self.root.contains(back)) {
        event.preventDefault();
        self.back();
        return;
      }
      var acc = event.target.closest('[data-rd-acc]');
      if (acc && self.root.contains(acc)) {
        event.preventDefault();
        self.toggleAccordion(acc);
        return;
      }
      var searchFocus = event.target.closest('[data-rd-search-focus]');
      if (searchFocus && self.root.contains(searchFocus)) {
        var input = self.root.querySelector('[data-rd-search-input]');
        if (input) {
          event.preventDefault();
          self.reset();
          focusSafely(input);
        }
      }
    });

    this.root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var close = self.host && self.host.querySelector('[data-mobile-nav-close]');
      if (close) {
        event.preventDefault();
        close.click();
      }
    });

    /* Empire sets data-open="true" on the host while the drawer is open.
       When it closes, go back to the department list for the next open. */
    if (this.host && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        if (self.host.getAttribute('data-open') !== 'true') self.reset();
      });
      observer.observe(this.host, { attributes: true, attributeFilter: ['data-open'] });
    }
  };

  /* ---- level push ---- */

  Drawer.prototype.go = function (id, opener) {
    if (!this.levels[id]) return;
    this.stack.push(this.current);
    this.openers[id] = opener || null;
    this.push(id, 'forward');
  };

  Drawer.prototype.back = function () {
    var previous = this.stack.length ? this.stack.pop() : 'root';
    var opener = this.openers[this.current];
    this.push(previous, 'back', opener);
  };

  Drawer.prototype.push = function (id, direction, focusTarget) {
    var next = this.levels[id];
    var previous = this.levels[this.current];
    if (!next || next === previous) return;
    if (this.animating) this.settle();

    var self = this;
    var stage = this.stage || next.parentNode;
    var forward = direction === 'forward';

    this.panel.scrollTop = 0;
    next.hidden = false;
    next.classList.add('is-active');

    if (!canAnimate(next)) {
      previous.hidden = true;
      previous.classList.remove('is-active');
      this.current = id;
      this.focusLevel(next, focusTarget);
      return;
    }

    // Lock the stage to the taller of the two so the outgoing level is never
    // clipped mid-slide and the pinned footer has nothing to react to.
    var height = Math.max(previous.offsetHeight, next.offsetHeight);
    stage.style.height = height + 'px';
    stage.classList.add('is-animating');
    (forward ? next : previous).style.zIndex = '2';

    var enter = next.animate(
      forward
        ? [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }]
        : [{ transform: 'translateX(-25%)', opacity: 0.5 }, { transform: 'translateX(0)', opacity: 1 }],
      { duration: PUSH_MS, easing: PUSH_EASE, fill: 'both' }
    );
    var exit = previous.animate(
      forward
        ? [{ transform: 'translateX(0)', opacity: 1 }, { transform: 'translateX(-25%)', opacity: 0.5 }]
        : [{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }],
      { duration: PUSH_MS, easing: PUSH_EASE, fill: 'both' }
    );

    this.current = id;
    this.animating = {
      next: next,
      previous: previous,
      stage: stage,
      enter: enter,
      exit: exit,
      focusTarget: focusTarget,
      timer: setTimeout(function () { self.settle(); }, PUSH_MS + 150)
    };
    enter.onfinish = function () { self.settle(); };
  };

  Drawer.prototype.settle = function () {
    var state = this.animating;
    if (!state) return;
    this.animating = null;
    clearTimeout(state.timer);
    state.enter.onfinish = null;
    try { state.enter.cancel(); } catch (error) { /* already gone */ }
    try { state.exit.cancel(); } catch (error) { /* already gone */ }

    state.next.style.zIndex = '';
    state.previous.style.zIndex = '';
    state.previous.hidden = true;
    state.previous.classList.remove('is-active');
    state.stage.classList.remove('is-animating');
    state.stage.style.height = '';

    this.panel.scrollTop = 0;
    this.focusLevel(state.next, state.focusTarget);
  };

  Drawer.prototype.focusLevel = function (level, focusTarget) {
    var target = focusTarget && level.contains(focusTarget) ? focusTarget : null;
    if (!target) target = level.querySelector('[data-rd-back]');
    if (!target) target = level.querySelector('a, button, input');
    focusSafely(target);
  };

  /* ---- group accordions ---- */

  /* Toggle a panel between its closed (height 0) and open (height auto)
     states, animating the rendered height between the two. Returns the
     panel's final height so the caller can plan the reveal scroll. */
  Drawer.prototype.setAccordion = function (button, open, animate) {
    var panel = document.getElementById(button.getAttribute('aria-controls'));
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!panel) return 0;

    var from = panel.getBoundingClientRect().height;
    if (panel.__rdAnim) {
      try { panel.__rdAnim.cancel(); } catch (error) { /* ignore */ }
      panel.__rdAnim = null;
    }
    panel.classList.toggle('is-open', open);
    var to = panel.getBoundingClientRect().height;

    if (animate && canAnimate(panel) && from !== to) {
      var anim = panel.animate(
        [{ height: from + 'px' }, { height: to + 'px' }],
        { duration: ACC_MS, easing: ACC_EASE }
      );
      panel.__rdAnim = anim;
      anim.onfinish = anim.oncancel = function () {
        if (panel.__rdAnim === anim) panel.__rdAnim = null;
      };
    }
    return to;
  };

  Drawer.prototype.toggleAccordion = function (button) {
    var isOpen = button.getAttribute('aria-expanded') === 'true';
    var level = button.closest('[data-rd-level]');
    var self = this;

    if (!isOpen && level) {
      toArray(level.querySelectorAll('[data-rd-acc][aria-expanded="true"]')).forEach(function (other) {
        if (other !== button) self.setAccordion(other, false, true);
      });
    }

    var finalHeight = this.setAccordion(button, !isOpen, true);
    if (!isOpen) this.revealAccordion(button, finalHeight);
  };

  /* Scroll just enough to bring the opened list into view, started together
     with the expansion so it reads as one motion, and never so far that the
     group's own row leaves the top of the drawer. */
  Drawer.prototype.revealAccordion = function (button, finalHeight) {
    var panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    var viewport = this.panel.getBoundingClientRect();
    var buttonRect = button.getBoundingClientRect();
    var footHeight = this.foot ? this.foot.offsetHeight : 0;
    var panelBottom = buttonRect.bottom + finalHeight;
    var overflow = panelBottom - (viewport.bottom - footHeight) + 16;
    if (overflow <= 0) return;

    var headroom = Math.max(0, buttonRect.top - viewport.top - 8);
    var delta = Math.min(overflow, headroom);
    if (delta <= 0) return;

    if (reducedMotion() || typeof this.panel.scrollBy !== 'function') {
      this.panel.scrollTop += delta;
      return;
    }
    try {
      this.panel.scrollBy({ top: delta, behavior: 'smooth' });
    } catch (error) {
      this.panel.scrollTop += delta;
    }
  };

  Drawer.prototype.reset = function () {
    if (this.animating) this.settle();
    var self = this;

    toArray(this.root.querySelectorAll('[data-rd-acc][aria-expanded="true"]')).forEach(function (button) {
      self.setAccordion(button, false, false);
    });

    this.stack = [];
    if (this.current !== 'root') {
      var current = this.levels[this.current];
      if (current) { current.hidden = true; current.classList.remove('is-active'); }
      var root = this.levels.root;
      if (root) { root.hidden = false; root.classList.add('is-active'); }
      this.current = 'root';
    }
    this.panel.scrollTop = 0;
  };

  /* ------------------------------------------------------------------ */
  function init(scope) {
    scope = scope || document;
    toArray(scope.querySelectorAll('[data-rd-nav]')).forEach(function (el) { new MegaNav(el); });
    toArray(scope.querySelectorAll('[data-rd-drawer]')).forEach(function (el) { new Drawer(el); });
  }

  /* Review helpers: ?rd_nav_open=2 opens the second desktop panel on load,
     ?rd_drawer=rd-l-1 opens the drawer at that department,
     ?rd_acc=rd-g-1-1 also expands that group. Harmless otherwise. */
  function applyReviewParams() {
    var search = window.location.search || '';
    var navMatch = /[?&]rd_nav_open=(\d+)/.exec(search);
    if (navMatch) {
      var nav = document.querySelector('[data-rd-nav]');
      var item = nav && nav.__rdNav && nav.__rdNav.items[parseInt(navMatch[1], 10) - 1];
      if (item) setTimeout(function () { nav.__rdNav.openItem(item); }, 300);
    }
    var drawerMatch = /[?&]rd_drawer=([\w-]+)/.exec(search);
    if (drawerMatch) {
      var toggle = document.querySelector('[data-menu-toggle]');
      var drawer = document.querySelector('[data-rd-drawer]');
      if (toggle && drawer && drawer.__rdDrawer) {
        setTimeout(function () {
          toggle.click();
          if (drawerMatch[1] !== 'root') {
            setTimeout(function () {
              drawer.__rdDrawer.go(drawerMatch[1]);
              var accMatch = /[?&]rd_acc=([\w-]+)/.exec(search);
              if (accMatch) {
                setTimeout(function () {
                  var button = drawer.querySelector('[aria-controls="' + accMatch[1] + '"]');
                  if (button) drawer.__rdDrawer.toggleAccordion(button);
                }, 500);
              }
            }, 500);
          }
        }, 400);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); applyReviewParams(); });
  } else {
    init();
    applyReviewParams();
  }

  /* Theme editor: re-bind when the header section reloads, and open the
     matching department panel when a feature-card block is selected. */
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block || !block.closest) return;
    var item = block.closest('[data-rd-nav-item]');
    if (!item) return;
    var nav = item.closest('[data-rd-nav]');
    if (nav && nav.__rdNav) nav.__rdNav.openItem(item);
  });
})();
